import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { Breadcrumbs } from "../../AbstractElements";
import { Card, CardBody, Col, Row } from "reactstrap";
import { DollarSign, Clock } from "react-feather";
import { updatePharmacyProfile } from "../Profile/Service/ManageClinicUsersService";
import { toast } from "react-toastify";

const ManageSetting = () => {
    const { t } = useTranslation();
    const { setLoading } = useLoader();
    const { pharmacyPOS, setPharmacyPOS } = usePharmacy();

    const [conversionRates, setConversionRates] = useState({});
    const [expiryAlertDays, setExpiryAlertDays] = useState("");

    const baseCurrency = pharmacyPOS?.baseCurrency || "INR";
    const allowedCurrencies = pharmacyPOS?.allowedCurrencies || [];

    /* ---------- INIT ---------- */
    useEffect(() => {
        if (pharmacyPOS) {
            setConversionRates(pharmacyPOS.conversionRates || {});
            setExpiryAlertDays(pharmacyPOS.expiry_alert_duration || "");
        }
    }, [pharmacyPOS]);

    /* ---------- HANDLERS ---------- */
    const handleRateChange = (currency, value) => {
        setConversionRates(prev => ({
            ...prev,
            [currency]: parseFloat(value) || 0
        }));
    };

    const saveConversionRates = async () => {
        setLoading(true);
        try {
            const payload = { conversionRates };
            const res = await updatePharmacyProfile(payload);

            if (res.data.status === "success") {
                toast.success(res.data.message || t("Currency conversion rates updated"));
                localStorage.setItem("currentPharmacyPOS", JSON.stringify(res.data.data));
                setPharmacyPOS(res.data.data);
            }
        } catch (err) {
            toast.warning(err || err.message || t("Failed to update conversion rates"));
        } finally {
            setLoading(false);
        }
    };

    const saveExpiryAlert = async () => {
        if (!expiryAlertDays || expiryAlertDays <= 0) {
            toast.info(t("Please enter valid expiry alert duration"));
            return;
        }

        setLoading(true);
        try {
            const payload = { expiry_alert_duration: Number(expiryAlertDays) };
            const res = await updatePharmacyProfile(payload);

            if (res.data.status === "success") {
                toast.success(t("Expiry alert duration updated"));
                localStorage.setItem("currentPharmacyPOS", JSON.stringify(res.data.data));
                setPharmacyPOS(res.data.data);
            }
        } catch (err) {
            toast.warning(t("Failed to update expiry alert"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Fragment>
            <Breadcrumbs
                mainTitle={t("Manage Settings")}
                parent={t("Dashboard")}
                title={t("Settings")}
            />

            <Card>
                <CardBody>
                    <Row>
                        {/* ================= Currency Conversion ================= */}
                        <Col md="6">
                            <div className="card border-light">
                                <div className="card-body">
                                    <h5 className="fw-bold mb-3">
                                        {t("Currency Conversion Rates")}
                                    </h5>

                                    <p className="text-muted">
                                        {t("Base Currency")}: <strong>{baseCurrency}</strong>
                                    </p>

                                    <Row className="g-3 align-items-center">
                                        {allowedCurrencies.map(currency => {
                                            if (currency === baseCurrency) return null;

                                            return (
                                                <Col md="12" key={currency}>
                                                    <label className="form-label fw-semibold">
                                                        {baseCurrency}
                                                    </label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">
                                                            1 {baseCurrency} =
                                                        </span>
                                                        <input
                                                            type="number"
                                                            placeholder="0"
                                                            value={conversionRates[currency] ?? ""}
                                                            onChange={(e) =>
                                                                handleRateChange(currency, e.target.value)
                                                            }
                                                            className="form-control"
                                                        />
                                                        <span className="input-group-text">
                                                            {currency}
                                                        </span>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>

                                    <div className="text-end mt-4">
                                        <button
                                            className="btn btn-primary"
                                            onClick={saveConversionRates}
                                        >
                                            {t("Save Conversion Rates")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        {/* ================= Expiry Alert ================= */}
                        <Col md="6">
                            <div className="card border-light">
                                <div className="card-body">
                                    <h5 className="fw-bold mb-3">
                                        <Clock size={18} className="me-2" />
                                        {t("Expiry Alert Settings")}
                                    </h5>

                                    <label className="form-label mb-2 fw-semibold">
                                        {t("Expiry Alert Duration (Days)")}
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        className="form-control"
                                        value={expiryAlertDays}
                                        onChange={(e) => setExpiryAlertDays(e.target.value)}
                                        placeholder={t("Enter number of days")}
                                    />

                                    <div className="text-end mt-4">
                                        <button
                                            className="btn btn-primary"
                                            onClick={saveExpiryAlert}
                                        >
                                            {t("Save Expiry Alert")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        </Fragment>
    );
};

export default ManageSetting;
